import * as Neutralino from '@neutralinojs/lib';

/**
 * Detecta si la aplicación se está ejecutando dentro de la app de escritorio (NeutralinoJS)
 * o en un navegador web tradicional (ej. Cloudflare Pages).
 */
export function isDesktop(): boolean {
  if (typeof window === 'undefined') return false;
  const isWebDomain = window.location.hostname.includes('pages.dev') ||
                      window.location.hostname.includes('facultad-track');
  return !isWebDomain || Boolean(
    (window as any).NL_TOKEN ||
    (window as any).isNeutralinoInitialized ||
    (window as any).Neutralino ||
    (window as any).NL_PORT ||
    (window as any).NL_ARGS
  );
}

/**
 * Lee un archivo del sistema local si estamos en desktop.
 * En la web retorna null silenciosamente.
 */
export async function readLocalFile(path: string): Promise<Uint8Array | string | null> {
  if (!isDesktop()) return null;
  try {
    const data = await Neutralino.filesystem.readBinaryFile(path);
    return new Uint8Array(data);
  } catch (err) {
    console.warn('[Platform] Error al leer archivo local:', err);
    return null;
  }
}

/**
 * Escribe un archivo en el sistema local si estamos en desktop.
 */
export async function writeLocalFile(path: string, data: Uint8Array | ArrayBuffer | string): Promise<boolean> {
  if (!isDesktop()) return false;
  try {
    let buffer: ArrayBuffer;
    if (typeof data === 'string') {
      const encoded = new TextEncoder().encode(data);
      buffer = encoded.buffer as ArrayBuffer;
    } else if (data instanceof Uint8Array) {
      buffer = data.buffer as ArrayBuffer;
    } else {
      buffer = data as ArrayBuffer;
    }
    await Neutralino.filesystem.writeBinaryFile(path, buffer);
    return true;
  } catch (err) {
    console.warn('[Platform] Error al escribir archivo local:', err);
    return false;
  }
}

/**
 * Muestra el diálogo nativo para seleccionar una carpeta.
 * En la web retorna null.
 */
export async function pickFolder(title = 'Seleccionar carpeta'): Promise<string | null> {
  if (!isDesktop()) return null;
  try {
    return await Neutralino.os.showFolderDialog(title);
  } catch (err) {
    console.warn('[Platform] Error al seleccionar carpeta:', err);
    return null;
  }
}

/**
 * Abre un archivo local o URL externa en el sistema operativo o navegador.
 */
export async function openExternal(urlOrPath: string): Promise<void> {
  if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
    window.open(urlOrPath, '_blank', 'noopener,noreferrer');
    return;
  }

  if (isDesktop()) {
    try {
      await Neutralino.os.open(urlOrPath);
    } catch (err) {
      console.warn('[Platform] Error al abrir enlace local nativo:', err);
    }
  } else {
    alert('Los archivos físicos locales solo están disponibles cuando usás la app de escritorio de Malla.');
  }
}

/**
 * Muestra el diálogo nativo para abrir archivo.
 */
export async function showOpenDialog(title = 'Seleccionar archivo'): Promise<string[] | null> {
  if (!isDesktop()) return null;
  try {
    const entries = await Neutralino.os.showOpenDialog(title, { multiSelections: false });
    return entries && entries.length > 0 ? entries : null;
  } catch (err) {
    console.warn('[Platform] Error en showOpenDialog:', err);
    return null;
  }
}

/**
 * Muestra el diálogo nativo para guardar archivo.
 */
export async function showSaveDialog(title = 'Guardar archivo', defaultName = ''): Promise<string | null> {
  if (!isDesktop()) return null;
  try {
    return await Neutralino.os.showSaveDialog(title, { defaultPath: defaultName });
  } catch (err) {
    console.warn('[Platform] Error en showSaveDialog:', err);
    return null;
  }
}

/**
 * Retorna la ruta nativa del sistema operativo (ej. carpeta data).
 */
export async function getOSPath(name: 'data' | 'documents' | 'downloads' = 'data'): Promise<string | null> {
  if (!isDesktop()) return null;
  try {
    return await Neutralino.os.getPath(name);
  } catch (err) {
    console.warn('[Platform] Error al obtener ruta OS:', err);
    return null;
  }
}
